import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import IngredientSelector from '../components/ingredients/IngredientSelector';
import SearchModeToggle from '../components/ingredients/SearchModeToggle';
import SearchResultsPanel from '../components/ingredients/SearchResultsPanel';
import { useIngredientSearch } from '../hooks/useIngredientSearch';
import {
  serializeStateToParams,
  deserializeParamsToState,
} from '../utils/ingredientSearchUtils';
import IngredientAPI from '../api/ingredient';

/**
 * IngredientSearch page component.
 * Orchestrates ingredient selection, mode toggling, and results display.
 * All state is persisted in URL query parameters for shareability.
 *
 * URL params: ?ingredients=uri1,uri2&mode=and|or&page=1
 *
 * Requirements: 2.1, 2.4, 2.6, 4.3, 4.4, 4.5, 4.6, 5.1, 5.2, 5.3, 5.4, 5.5, 5.6, 6.2
 */
export default function IngredientSearch() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse URL state
  const { uris, mode: urlMode, page: urlPage } = useMemo(
    () => deserializeParamsToState(searchParams),
    [searchParams]
  );

  // Selected ingredients with full objects (uri + name)
  const [selectedIngredients, setSelectedIngredients] = useState([]);
  // URIs that were in the URL but could not be validated against the API
  // eslint-disable-next-line no-unused-vars
  const [invalidUris, setInvalidUris] = useState([]);
  // Warning message for invalid URIs
  const [validationWarning, setValidationWarning] = useState('');
  // Whether initial URI validation is in progress
  const [isValidating, setIsValidating] = useState(false);
  // Track whether initial validation has been completed
  const initialValidationDone = useRef(false);
  // Track previous ingredients/mode for page reset logic
  const prevIngredientsRef = useRef(null);
  const prevModeRef = useRef(null);
  // Ref for scrolling to top of results
  const resultsRef = useRef(null);

  // Derive mode - default to 'and' if invalid
  const mode = (urlMode === 'and' || urlMode === 'or') ? urlMode : 'and';

  // Current page from URL (will be clamped after results load)
  const [currentPage, setCurrentPage] = useState(urlPage);

  // Selected ingredient URIs for the search hook
  const selectedUris = useMemo(
    () => selectedIngredients.map((ing) => ing.uri),
    [selectedIngredients]
  );

  // Use the search hook
  const { results, pagination, isLoading, error, retry } = useIngredientSearch(
    selectedUris,
    mode,
    currentPage
  );

  // Clamp page to valid range when results come back (Req 4.6)
  useEffect(() => {
    if (pagination.totalPages > 0 && currentPage > pagination.totalPages) {
      setCurrentPage(pagination.totalPages);
    }
  }, [pagination.totalPages, currentPage]);

  // Validate ingredient URIs from URL on initial load (Req 5.5)
  useEffect(() => {
    if (initialValidationDone.current) return;
    if (uris.length === 0) {
      initialValidationDone.current = true;
      return;
    }

    initialValidationDone.current = true;
    setIsValidating(true);

    const validateUris = async () => {
      const validIngredients = [];
      const invalid = [];

      await Promise.all(
        uris.map(async (uri) => {
          try {
            const data = await IngredientAPI.getOne(uri);
            if (data && data.name) {
              validIngredients.push({ uri, name: data.name });
            } else {
              invalid.push(uri);
            }
          } catch {
            invalid.push(uri);
          }
        })
      );

      setSelectedIngredients(validIngredients);
      setInvalidUris(invalid);

      if (invalid.length > 0) {
        setValidationWarning(
          `${invalid.length} ingredient${invalid.length > 1 ? 's' : ''} could not be found and ${invalid.length > 1 ? 'were' : 'was'} removed from the selection.`
        );
      }

      setIsValidating(false);
    };

    validateUris();
  }, [uris]);

  // Reset page to 1 when ingredients or mode change (Req 4.5)
  useEffect(() => {
    const currentIngredientsKey = JSON.stringify(selectedUris);
    const prevIngredientsKey = prevIngredientsRef.current;
    const prevMode = prevModeRef.current;

    // Skip on initial render
    if (prevIngredientsKey === null && prevMode === null) {
      prevIngredientsRef.current = currentIngredientsKey;
      prevModeRef.current = mode;
      return;
    }

    if (currentIngredientsKey !== prevIngredientsKey || mode !== prevMode) {
      setCurrentPage(1);
    }

    prevIngredientsRef.current = currentIngredientsKey;
    prevModeRef.current = mode;
  }, [selectedUris, mode]);

  // Update URL when state changes (with history replacement) (Req 5.1, 5.2)
  useEffect(() => {
    if (isValidating) return;

    const params = serializeStateToParams(selectedIngredients, mode, currentPage);
    setSearchParams(params, { replace: true });
  }, [selectedIngredients, mode, currentPage, setSearchParams, isValidating]);

  // Scroll to top of results when page changes (Req 4.3)
  const prevPageRef = useRef(currentPage);
  useEffect(() => {
    if (prevPageRef.current !== currentPage && resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    prevPageRef.current = currentPage;
  }, [currentPage]);

  // Handle ingredient selection
  const handleSelectIngredient = useCallback((ingredient) => {
    setSelectedIngredients((prev) => {
      // Prevent duplicates (Req 1.7)
      if (prev.some((ing) => ing.uri === ingredient.uri)) {
        return prev;
      }
      return [...prev, ingredient];
    });
    // Clear any previous validation warning
    setValidationWarning('');
  }, []);

  // Handle ingredient removal
  const handleRemoveIngredient = useCallback((uri) => {
    setSelectedIngredients((prev) => prev.filter((ing) => ing.uri !== uri));
  }, []);

  // Handle mode change (Req 2.4)
  const handleModeChange = useCallback((newMode) => {
    // Update URL by re-serializing with new mode
    const params = serializeStateToParams(selectedIngredients, newMode, 1);
    setSearchParams(params, { replace: true });
  }, [selectedIngredients, setSearchParams]);

  // Handle page change from SearchResultsPanel
  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
  }, []);

  // Dismiss validation warning
  const handleDismissWarning = useCallback(() => {
    setValidationWarning('');
  }, []);

  return (
    <PageLayout
      pageTitle="Search by Ingredients"
      title="Search by Ingredients"
      subtitle="Find products by selecting active ingredients"
      showSearch={false}
      showGradient={true}
    >
      <div className="pb-12">
        {/* Validation warning for invalid URIs from URL (Req 5.5) */}
        {validationWarning && (
          <div className="flex items-center justify-between gap-3 mb-4 px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-14">
            <span>{validationWarning}</span>
            <button
              type="button"
              onClick={handleDismissWarning}
              className="flex-shrink-0 px-2 py-1 text-12 font-medium rounded hover:bg-amber-100 transition-colors"
              aria-label="Dismiss warning"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Ingredient Selector */}
        <div className="mb-6">
          <IngredientSelector
            selectedIngredients={selectedIngredients}
            onSelect={handleSelectIngredient}
            onRemove={handleRemoveIngredient}
            maxSelections={10}
            disabled={isValidating}
          />
        </div>

        {/* Search Mode Toggle - disabled when fewer than 2 ingredients (Req 2.6) */}
        <div className="mb-6 flex items-center gap-3">
          <SearchModeToggle
            mode={mode}
            onChange={handleModeChange}
            disabled={selectedIngredients.length < 2}
          />
          {selectedIngredients.length < 2 && selectedIngredients.length > 0 && (
            <span className="text-14 text-secondary/50">
              Select at least 2 ingredients to switch mode
            </span>
          )}
        </div>

        {/* Search Results Panel */}
        <div ref={resultsRef}>
          <SearchResultsPanel
            results={results}
            pagination={pagination}
            isLoading={isLoading || isValidating}
            error={error}
            mode={mode}
            selectedIngredientUris={selectedUris}
            onPageChange={handlePageChange}
            onRetry={retry}
            hasIngredients={selectedIngredients.length > 0}
          />
        </div>
      </div>
    </PageLayout>
  );
}
